import { Button } from '@/components/ui/button';
import { useCandidate, useCandidateFiles } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';
import type { CandidateFile } from '@/types/candidates';
import { Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useCandidateParseJob } from '../context/candidate-parse-job-context';
import { FILE_TYPE_GROUP_ORDER } from '../lib/file-labels';
import { CandidateFileGroup } from './candidate-file-group';
import { CandidateFilePreviewDialog } from './candidate-file-preview-dialog';
import { CandidateFileUploadDialog } from './candidate-file-upload-dialog';

interface CandidateFilesTabProps {
  candidateId: string;
}

export function CandidateFilesTab({ candidateId }: CandidateFilesTabProps) {
  const { data, isLoading, error } = useCandidateFiles(candidateId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<CandidateFile | null>(null);
  const { activeParseJobId } = useCandidateParseJob();
  // HW-1055: a candidate who revoked consent gets no new files.
  const locked = isConsentLocked(useCandidate(candidateId).data);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium">Files</h2>
          <p className="text-sm text-muted-foreground">
            Versioned CVs, test tasks, portfolio and other attachments.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setUploadOpen(true)} disabled={locked}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Upload file
        </Button>
      </div>

      {activeParseJobId ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
          <Sparkles className="h-4 w-4 animate-pulse" />
          <span>Parsing CV — auto-filling contacts and appending skills…</span>
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-md border border-border bg-background p-4 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load files.
        </div>
      ) : (
        <div className="space-y-2">
          {FILE_TYPE_GROUP_ORDER.map((t) => (
            <CandidateFileGroup
              key={t}
              candidateId={candidateId}
              fileType={t}
              files={data?.[t] ?? []}
              onPreview={(f) => setPreviewFile(f)}
            />
          ))}
        </div>
      )}

      <CandidateFileUploadDialog
        candidateId={candidateId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />

      <CandidateFilePreviewDialog
        file={previewFile}
        open={!!previewFile}
        onOpenChange={(open) => {
          if (!open) setPreviewFile(null);
        }}
      />
    </section>
  );
}
